import { Badge } from "@/components/ui/badge";
import type { ContactLanguageSource, SupportedLanguage } from "./contracts";
import {
  formatLanguage,
  isDefaultLanguage,
  languageLabel,
  languageSourceLabel,
} from "./languages";

/**
 * 🌐 chip for a non-English language, shared by the contact list ("preferred")
 * and the Echo pages ("content"). Renders nothing at all for null / "en": an
 * absent value is the default English, which is every contact and Echo made
 * before this feature and every backend that does not project the field — so
 * the rows must look exactly as they did before.
 *
 * The endonym is the visible label ("italiano"); the English name, the code
 * and the provenance ride in the tooltip so the badge row does not reflow.
 */
export const LanguageBadge = ({
  code,
  source,
  kind,
  supported,
  className,
}: {
  code?: string | null;
  source?: ContactLanguageSource | null;
  /** What the language applies to — only changes the tooltip wording. */
  kind: "preferred" | "content";
  supported?: readonly SupportedLanguage[] | null;
  className?: string;
}) => {
  if (isDefaultLanguage(code)) return null;

  const language = languageLabel(code, supported);
  const sourceLabel = languageSourceLabel(source);
  const what =
    kind === "content" ? "Echo content language" : "Preferred language";
  const title = `${what}: ${formatLanguage(language)} (${language.code})${
    sourceLabel ? ` · ${sourceLabel}` : ""
  }`;

  return (
    <Badge
      variant="outline"
      className={className}
      title={title}
      aria-label={title}
    >
      🌐 {language.endonym}
    </Badge>
  );
};
